'use client';

import React from 'react';

import { cn } from '../../utils/cn';

import type { HTMLAttributes, ReactNode } from 'react';

export interface UzDescriptionItem {
  term: ReactNode;
  description: ReactNode;
}

export interface UzDescriptionListProps extends Omit<HTMLAttributes<HTMLDListElement>, 'children'> {
  items: ReadonlyArray<UzDescriptionItem>;
  /** Two-column layout (term left, description right). */
  layout?: 'inline' | 'stacked';
  /** Visual density. */
  density?: 'compact' | 'comfortable';
}

/**
 * Key/value list. Use for record details, settings summaries, info panels.
 * Easier to consume than a one-off <table>.
 */
export const UzDescriptionList = ({
  items,
  layout = 'inline',
  density = 'comfortable',
  className,
  ...rest
}: UzDescriptionListProps): React.JSX.Element => (
  <dl
    className={cn(
      'divide-y',
      density === 'compact' && 'text-sm',
      density === 'comfortable' && 'text-sm',
      className,
    )}
    {...rest}
  >
    {items.map((it, i) => (
      <div
        key={i}
        className={cn(
          density === 'compact' ? 'py-2' : 'py-3',
          layout === 'inline' ? 'grid grid-cols-3 gap-4' : 'space-y-1',
        )}
      >
        <dt
          className={cn('text-muted-foreground font-medium', layout === 'inline' && 'col-span-1')}
        >
          {it.term}
        </dt>
        <dd className={cn('text-foreground', layout === 'inline' && 'col-span-2')}>
          {it.description}
        </dd>
      </div>
    ))}
  </dl>
);
